import React, { useState, useEffect } from 'react';
import ConfirmationDialog from '../ConfirmationDialog';
import commonUnits, { groupedUnits } from './Units';
import { DefaultHelpTips } from './DefaultHelpTips';
import '../../styles.css';

function ParameterEditor({ param, groupIdx, paramIdx, setTabs, nonRemovableNames, isNew = false, currentTab }) {
  const [showNewParam, setShowNewParam] = useState(false);
  const [newParam, setNewParam] = useState(isNew ? { name: '', unit: '°C', min: '', max: '', step: '', leftRight: false, help: '', value: '', isFourCornerGroup: false, isFrontRearGroup: false, colorThresholds: { cold: '', warm: '', hot: '', tooHot: '', ridiculouslyHot: '' } } : { ...param, groupIdx, paramIdx });
  const [showDeleteParamConfirm, setShowDeleteParamConfirm] = useState(false);
  const [pendingDeleteParam, setPendingDeleteParam] = useState(null);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [showInfoDialog, setShowInfoDialog] = useState(false);

  useEffect(() => {
    if (!isNew) {
      setNewParam({ ...param, groupIdx, paramIdx, colorThresholds: param.colorThresholds || { cold: '', warm: '', hot: '', tooHot: '', ridiculouslyHot: '' } });
    }
  }, [param, groupIdx, paramIdx, isNew]);

  const addParameter = () => {
    if (!newParam.name) {
      setValidationMessage('Parameter name is required');
      setShowValidationDialog(true);
      return;
    }
    if (newParam.unit !== 'text' && newParam.min && newParam.max && parseFloat(newParam.max) < parseFloat(newParam.min)) {
      setValidationMessage('Max value cannot be lower than min value. Please fix this before saving.');
      setShowValidationDialog(true);
      return;
    }
    const paramToAdd = { ...newParam };
    // Set type for known select parameters
    if (['Tire ID', 'Drive Type', 'Number of Gears'].includes(paramToAdd.name)) {
      paramToAdd.type = 'select';
    }
    // Auto-insert default help if no custom help provided
    if (!paramToAdd.help && DefaultHelpTips[paramToAdd.name]) {
      paramToAdd.help = DefaultHelpTips[paramToAdd.name];
    }
    setTabs(prevTabs => {
      const allParams = prevTabs.flatMap(tab => tab.groups.flatMap(group => group.params));
      const existingNames = allParams.map(p => p.name.toLowerCase());
      if (existingNames.includes(paramToAdd.name.toLowerCase())) {
        setShowDuplicateDialog(true);
        return prevTabs;
      }
      const updatedTabs = [...prevTabs];
      const targetGroupIdx = updatedTabs[currentTab].groups.length - 1;
      const group = updatedTabs[currentTab].groups[targetGroupIdx];
      group.params.push(paramToAdd);
      setShowNewParam(false);
      setNewParam({ name: '', unit: 'mm', min: '', max: '', step: '', leftRight: false, help: '', value: '', isFourCornerGroup: false, isFrontRearGroup: false });
      return updatedTabs;
    });
  };

  const updateParamField = (field, value) => {
    setTabs(prevTabs => {
      const updatedTabs = [...prevTabs];
      updatedTabs[currentTab].groups[groupIdx].params[paramIdx][field] = value;
      return updatedTabs;
    });
    setNewParam({ ...newParam, [field]: value });
  };

  const saveEditParam = () => {
    if (!newParam.name) {
      setValidationMessage('Parameter name is required');
      setShowValidationDialog(true);
      return;
    }
    if (newParam.unit !== 'text' && newParam.min && newParam.max && parseFloat(newParam.max) < parseFloat(newParam.min)) {
      setValidationMessage('Max value cannot be lower than min value. Please fix this before saving.');
      setShowValidationDialog(true);
      return;
    }
    setTabs(prevTabs => {
      const updatedTabs = [...prevTabs];
      updatedTabs[currentTab].groups[groupIdx].params[paramIdx] = { ...newParam };
      return updatedTabs;
    });
    setShowNewParam(false);
  };

  const deleteParam = () => {
    if (nonRemovableNames?.includes(param.name)) {
      return; // Don't delete the mandatory param
    }
    setPendingDeleteParam({ groupIdx, paramIdx });
    setShowDeleteParamConfirm(true);
  };

  const confirmDeleteParam = () => {
    setTabs(prevTabs => {
      const updatedTabs = [...prevTabs];
      updatedTabs[currentTab].groups[groupIdx].params.splice(paramIdx, 1);
      return updatedTabs;
    });
    setShowDeleteParamConfirm(false);
    setPendingDeleteParam(null);
  };

  return (
    <>
      {isNew ? (
        <button onClick={() => setShowNewParam(true)} className="btn btn-save">Add Parameter (+)</button>
      ) : (
        <>
          <i
            className="material-icons param-edit-icon"
            onClick={() => setShowNewParam(true)}
          >
            settings
          </i>
          <div className="param-action-placeholder">
            {(() => {
              const isMandatory = nonRemovableNames?.includes(param.name);
              return !isMandatory && (
                <button onClick={deleteParam} className="btn btn-group-action delete">−</button>
              );
            })()}
          </div>
        </>
      )}
      {showNewParam && param?.isDamperGraph ? (
        <div className="overlay-card">
          <div className="card-container">
            <div className="page-title" style={{ paddingBottom: '20px' }}>
              <h1>Damper Graph Settings</h1>
              <button onClick={() => setShowNewParam(false)} className="close-button">✕</button>
            </div>
            <div style={{ marginBottom: '10px', marginLeft: '8px', marginRight: '12px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Speed Unit</label>
              <select
                value={newParam.speedUnit || 'mm/s'}
                onChange={e => updateParamField('speedUnit', e.target.value)}
                className="event-editor-select"
              >
                <option value="mm/s">mm/s</option>
                <option value="in/s">in/s</option>
              </select>
            </div>
            <div style={{ marginBottom: '10px', marginLeft: '8px', marginRight: '12px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Force Unit</label>
              <select
                value={newParam.forceUnit || 'N'}
                onChange={e => updateParamField('forceUnit', e.target.value)}
                className="event-editor-select"
              >
                <option value="N">N</option>
                <option value="lb">lb</option>
              </select>
            </div>
            <div className="event-editor-actions">
              <button onClick={() => setShowNewParam(false)} className="btn btn-save">Done</button>
            </div>
          </div>
        </div>
      ) : showNewParam && (
        <div className="overlay-card">
          <div className="card-container">
            <div className="page-title" style={{ paddingBottom: '20px' }}>
              <h1>{isNew ? 'New Parameter' : 'Edit Parameter'}</h1>
              <button onClick={() => setShowNewParam(false)} className="close-button">✕</button>
            </div>
            <input
              value={newParam.name}
              onChange={e => setNewParam({ ...newParam, name: e.target.value })}
              placeholder="Parameter Name"
              className="event-editor-input"
              disabled={!isNew && nonRemovableNames?.includes(newParam.name)}
            />
            <select
              value={newParam.unit}
              onChange={e => (isNew ? setNewParam({ ...newParam, unit: e.target.value }) : updateParamField('unit', e.target.value))}
              className="event-editor-select"
            >
              {newParam.name === 'Tire Temperature' ? (
                <>
                  <option value="°C">°C</option>
                  <option value="°F">°F</option>
                </>
              ) : (
                groupedUnits.map(group => {
                  if (group.label === 'Unitless' || group.label === 'Text') {
                    return group.units.map(u => (
                      <option
                        key={u}
                        value={u}
                        style={group.style}
                      >
                        {u === '' ? 'Unitless' : u}
                      </option>
                    ));
                  } else {
                    return (
                      <optgroup key={group.label} label={group.label}>
                        {group.units.map(u => (
                          <option
                            key={u}
                            value={u}
                            style={group.style}
                          >
                            {u === '' ? 'Unitless' : u}
                          </option>
                        ))}
                      </optgroup>
                    );
                  }
                })
              )}
            </select>
            <div className="input-with-icon">
              <i className="material-icons input-icon">vertical_align_bottom</i>
              <input
                value={newParam.min}
                onChange={e => (isNew ? setNewParam({ ...newParam, min: e.target.value }) : updateParamField('min', e.target.value))}
                placeholder="Min"
                className="event-editor-input"
                type="number"
                disabled={newParam.unit === 'text'}
              />
            </div>
            <div className="input-with-icon">
              <i className="material-icons input-icon">vertical_align_top</i>
              <input
                value={newParam.max}
                onChange={e => (isNew ? setNewParam({ ...newParam, max: e.target.value }) : updateParamField('max', e.target.value))}
                placeholder="Max"
                className="event-editor-input"
                type="number"
                disabled={newParam.unit === 'text'}
              />
            </div>
            <div className="input-with-icon">
              <i className="material-icons input-icon">stairs_2</i>
              <input
                value={newParam.step}
                onChange={e => (isNew ? setNewParam({ ...newParam, step: e.target.value }) : updateParamField('step', e.target.value))}
                placeholder="Step"
                className="event-editor-input"
                type="number"
                step="0.01"
                disabled={newParam.unit === 'text'}
              />
            </div>
            {newParam.name === 'Tire Temperature' && (
              <div style={{ marginBottom: '10px', marginLeft: '8px', marginRight: '12px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Tire Colors
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <div className="input-with-icon">
                    <label style={{ width: '80px', display: 'inline-block' }}>Cold:</label>
                    <input
                      value={newParam.colorThresholds?.cold || ''}
                      onChange={e => {
                        const value = { ...newParam.colorThresholds, cold: e.target.value };
                        setNewParam({ ...newParam, colorThresholds: value });
                        if (!isNew) updateParamField('colorThresholds', value);
                      }}
                      placeholder="Cold"
                      className="event-editor-input"
                      type="number"
                      step="0.1"
                    />
                  </div>
                  <div className="input-with-icon">
                    <label style={{ width: '80px', display: 'inline-block' }}>Warm:</label>
                    <input
                      value={newParam.colorThresholds?.warm || ''}
                      onChange={e => {
                        const value = { ...newParam.colorThresholds, warm: e.target.value };
                        setNewParam({ ...newParam, colorThresholds: value });
                        if (!isNew) updateParamField('colorThresholds', value);
                      }}
                      placeholder="Warm"
                      className="event-editor-input"
                      type="number"
                      step="0.1"
                    />
                  </div>
                  <div className="input-with-icon">
                    <label style={{ width: '80px', display: 'inline-block' }}>Hot:</label>
                    <input
                      value={newParam.colorThresholds?.hot || ''}
                      onChange={e => {
                        const value = { ...newParam.colorThresholds, hot: e.target.value };
                        setNewParam({ ...newParam, colorThresholds: value });
                        if (!isNew) updateParamField('colorThresholds', value);
                      }}
                      placeholder="Hot"
                      className="event-editor-input"
                      type="number"
                      step="0.1"
                    />
                  </div>
                  <div className="input-with-icon">
                    <label style={{ width: '80px', display: 'inline-block' }}>Too Hot:</label>
                    <input
                      value={newParam.colorThresholds?.tooHot || ''}
                      onChange={e => {
                        const value = { ...newParam.colorThresholds, tooHot: e.target.value };
                        setNewParam({ ...newParam, colorThresholds: value });
                        if (!isNew) updateParamField('colorThresholds', value);
                      }}
                      placeholder="Too Hot"
                      className="event-editor-input"
                      type="number"
                      step="0.1"
                    />
                  </div>
                  <div className="input-with-icon">
                    <label style={{ width: '80px', display: 'inline-block' }}>Ridiculously Hot:</label>
                    <input
                      value={newParam.colorThresholds?.ridiculouslyHot || ''}
                      onChange={e => {
                        const value = { ...newParam.colorThresholds, ridiculouslyHot: e.target.value };
                        setNewParam({ ...newParam, colorThresholds: value });
                        if (!isNew) updateParamField('colorThresholds', value);
                      }}
                      placeholder="Ridiculously Hot"
                      className="event-editor-input"
                      type="number"
                      step="0.1"
                    />
                  </div>
                </div>
              </div>
            )}
            {newParam.name !== 'Tire Temperature' && (
              <div style={{ marginBottom: '10px', marginLeft: '8px', marginRight: '12px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Parameter Type
                  <i
                    className="material-icons"
                    style={{ fontSize: '16px', marginLeft: '5px', color: '#666', cursor: 'pointer' }}
                    onClick={() => setShowInfoDialog(true)}
                    title="Click for more info about parameter types"
                  >
                    info
                  </i>
                </label>
              <div className="segmented-control">
                <button
                  type="button"
                  className={`segmented-button ${!newParam.leftRight && !newParam.isFourCornerGroup && !newParam.isFrontRearGroup ? 'active' : ''}`}
                  onClick={() => {
                    const value = { leftRight: false, isFourCornerGroup: false, isFrontRearGroup: false };
                    setNewParam({ ...newParam, ...value });
                    if (!isNew) {
                      Object.keys(value).forEach(key => updateParamField(key, value[key]));
                    }
                  }}
                  disabled={!isNew}
                >
                  Single
                </button>
                <button
                  type="button"
                  className={`segmented-button ${newParam.leftRight ? 'active' : ''}`}
                  onClick={() => {
                    const value = { leftRight: true, isFourCornerGroup: false, isFrontRearGroup: false };
                    setNewParam({ ...newParam, ...value });
                    if (!isNew) {
                      Object.keys(value).forEach(key => updateParamField(key, value[key]));
                    }
                  }}
                  disabled={!isNew}
                >
                  Left/Right
                </button>
                <button
                  type="button"
                  className={`segmented-button ${newParam.isFrontRearGroup ? 'active' : ''}`}
                  onClick={() => {
                    const value = { leftRight: false, isFourCornerGroup: false, isFrontRearGroup: true };
                    setNewParam({ ...newParam, ...value });
                    if (!isNew) {
                      Object.keys(value).forEach(key => updateParamField(key, value[key]));
                    }
                  }}
                  disabled={!isNew}
                >
                  Front/Rear
                </button>
                <button
                  type="button"
                  className={`segmented-button ${newParam.isFourCornerGroup ? 'active' : ''}`}
                  onClick={() => {
                    const value = { leftRight: false, isFourCornerGroup: true, isFrontRearGroup: false };
                    setNewParam({ ...newParam, ...value });
                    if (!isNew) {
                      Object.keys(value).forEach(key => updateParamField(key, value[key]));
                    }
                  }}
                  disabled={!isNew}
                >
                  Four Corner
                </button>
              </div>
            </div>
            )}
            <textarea
              value={newParam.help}
              onChange={e => (isNew ? setNewParam({ ...newParam, help: e.target.value }) : updateParamField('help', e.target.value))}
              placeholder="Help Tips"
              className="event-editor-textarea"
            />
            <div className="event-editor-actions">
              <button onClick={isNew ? addParameter : saveEditParam} className="btn btn-save">Save</button>
              <button onClick={() => setShowNewParam(false)} className="btn btn-secondary">Cancel</button>
            </div>
            {showDuplicateDialog && (
              <div className="confirmation-dialog-overlay">
                <div className="confirmation-dialog">
                  <p>Parameter name already in use, please choose a new name</p>
                  <div className="confirmation-dialog-actions">
                    <button onClick={() => setShowDuplicateDialog(false)} className="btn btn-save">OK</button>
                  </div>
                </div>
              </div>
            )}
            {showValidationDialog && (
              <div className="confirmation-dialog-overlay">
                <div className="confirmation-dialog">
                  <p>{validationMessage}</p>
                  <div className="confirmation-dialog-actions">
                    <button onClick={() => setShowValidationDialog(false)} className="btn btn-save">OK</button>
                  </div>
                </div>
              </div>
            )}
            {showInfoDialog && (
              <div className="confirmation-dialog-overlay">
                <div className="confirmation-dialog">
                  <p style={{ textAlign: 'left' }}>Parameter Type can only be set when the Parameter is created. If you'd like to change the Parameter Type, delete and then re-add the Parameter.</p>
                  <div style={{ display: 'flex', alignItems: 'flex-start', marginTop: '10px', padding: '10px', backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '4px' }}>
                    <i className="material-icons" style={{ color: '#856404', marginRight: '8px', marginTop: '2px' }}>warning</i>
                    <div style={{ textAlign: 'left' }}>
                      <p style={{ margin: '0', fontWeight: 'bold', color: '#856404' }}>Keep in mind that historical data and comparisons will no longer match once you've changed the Parameter Type or Name.</p>
                      <p style={{ margin: '5px 0 0 0', color: '#856404', textAlign: 'left' }}>To match to historical data that was previously a grouped Parameter Type (such as Left/Right or Four Corner), you can save Single parameters as follows:</p>
                      <ul style={{ margin: '5px 0 0 20px', color: '#856404', textAlign: 'left', paddingLeft: '20px' }}>
                        <li>Parameter Name (Front)</li>
                        <li>Parameter Name (Left)</li>
                        <li>Parameter Name (Front Left)</li>
                        <li>etc.</li>
                      </ul>
                    </div>
                  </div>
                  <div className="confirmation-dialog-actions">
                    <button onClick={() => setShowInfoDialog(false)} className="btn btn-save">OK</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {showDeleteParamConfirm && (
        <ConfirmationDialog
          message="Are you sure you want to delete this parameter?"
          onConfirm={confirmDeleteParam}
          onCancel={() => {
            setShowDeleteParamConfirm(false);
            setPendingDeleteParam(null);
          }}
        />
      )}
    </>
  );
}

export default ParameterEditor;